import React, { useState, useEffect, useRef } from 'react'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import CustomMarkdownEditor from 'components/CustomMarkdownEditor'
import AIPlannerMarkdownRenderer from 'features/planner/components/AIPlannerMarkdownRenderer'
import BlueprintEditWarning from 'features/planner/components/BlueprintEditWarning'

interface EditFieldFlyoutProps {
  fieldKey: string
  fieldLabel: string
  fieldValue: string
  onSave: (fieldKey: string, value: string) => void
  onClose: () => void
  isUpdating: boolean
  showBlueprintWarning?: boolean
}

const EditFieldFlyout: React.FC<EditFieldFlyoutProps> = ({ fieldKey, fieldLabel, fieldValue, onSave, onClose, isUpdating, showBlueprintWarning = false }) => {
  const [value, setValue] = useState(fieldValue)
  const [bodyHeight, setBodyHeight] = useState('calc(100vh - 200px)')

  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const hasChanges = value !== fieldValue

  useEffect(() => {
    const calculateBodyHeight = () => {
      const headerHeight = headerRef.current?.clientHeight || 0
      const footerHeight = footerRef.current?.clientHeight || 0
      const totalReservedHeight = headerHeight + footerHeight + 40

      setBodyHeight(`calc(100vh - ${totalReservedHeight}px)`)
    }

    const timer = setTimeout(calculateBodyHeight, 100)

    const handleResize = () => {
      calculateBodyHeight()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleSave = () => {
    onSave(fieldKey, value)
  }

  return (
    <AcFlyout onClose={onClose} size="l">
      <EuiFlyoutHeader>
        <div ref={headerRef}>
          <EuiTitle size="m">
            <h2>Edit {fieldLabel}</h2>
          </EuiTitle>
        </div>
      </EuiFlyoutHeader>
      <EuiFlyoutBody style={{
        overflow: 'hidden',
        height: bodyHeight,
        maxHeight: bodyHeight
      }}>
        <BlueprintEditWarning show={showBlueprintWarning} />
        <CustomMarkdownEditor
          height={bodyHeight}
          value={value}
          onChange={setValue}
          readOnly={isUpdating}
          previewRenderer={AIPlannerMarkdownRenderer}
        />
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <div ref={footerRef}>
          <EuiFlexGroup direction="row" alignItems="center">
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onClose} isDisabled={isUpdating}>
                Cancel
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={handleSave} isLoading={isUpdating} isDisabled={!hasChanges}>
                Save
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </div>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditFieldFlyout
